// src/Pages/PharmacistDashboard/NotificationPage.js

import React, { useState } from 'react';
import {
  Box,
  Typography,
  Grid,
  Paper,
  List,
  ListItem,
  ListItemText,
  IconButton,
  Tooltip,
  Button,
  Divider,
  TextField,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Badge,
  Snackbar,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  MarkEmailRead as MarkReadIcon,
  Delete as DeleteIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  ClearAll as ClearAllIcon,
} from '@mui/icons-material';
import { pharmacistNotificationMockData } from '../../data/pharmacistNotificationMockData.js'; // Mock data

const PharmacistNotificationPage = () => {
  const [notifications, setNotifications] = useState(
    pharmacistNotificationMockData
  );
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('');
  const [notification, setNotification] = useState(null);

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const handleSearchChange = (event) => {
    setSearchTerm(event.target.value);
  };

  const handleFilterChange = (event) => {
    setFilterType(event.target.value);
  };

  const handleMarkAsRead = (id) => {
    setNotifications((prevNotifications) =>
      prevNotifications.map((notification) =>
        notification.id === id ? { ...notification, read: true } : notification
      )
    );
    setNotification({
      message: 'Notification marked as read',
      severity: 'info',
    });
  };

  const handleDeleteNotification = (id) => {
    setNotifications((prevNotifications) =>
      prevNotifications.filter((notification) => notification.id !== id)
    );
    setNotification({ message: 'Notification deleted', severity: 'success' });
  };

  const handleClearAllNotifications = () => {
    setNotifications([]);
    setNotification({
      message: 'All notifications cleared',
      severity: 'warning',
    });
  };

  const filteredNotifications = notifications.filter(
    (notification) =>
      notification.type.includes(filterType) &&
      (notification.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        notification.sender.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <Box sx={{ padding: 2 }}>
      <Typography variant='h4' gutterBottom>
        Pharmacy Notifications
      </Typography>

      {/* Search and Filters */}
      <Grid container spacing={2} sx={{ marginBottom: 2 }}>
        <Grid item xs={12} md={6}>
          <TextField
            fullWidth
            label='Search Notifications'
            variant='outlined'
            value={searchTerm}
            onChange={handleSearchChange}
            InputProps={{
              endAdornment: <SearchIcon />,
            }}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <FormControl fullWidth>
            <InputLabel>Filter by Type</InputLabel>
            <Select
              value={filterType}
              onChange={handleFilterChange}
              label='Filter by Type'
            >
              <MenuItem value=''>All</MenuItem>
              <MenuItem value='Prescription'>Prescription Updates</MenuItem>
              <MenuItem value='Inventory'>Inventory Alerts</MenuItem>
              <MenuItem value='Supplier'>Supplier Notifications</MenuItem>
              <MenuItem value='Alert'>Urgent Alerts</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          xs={12}
          md={2}
          sx={{ display: 'flex', justifyContent: 'flex-end' }}
        >
          <Button
            variant='contained'
            color='error'
            startIcon={<ClearAllIcon />}
            onClick={handleClearAllNotifications}
            fullWidth
          >
            Clear All
          </Button>
        </Grid>
      </Grid>

      <Divider sx={{ my: 4 }} />

      {/* Notifications List */}
      <Paper
        elevation={2}
        sx={{ maxHeight: isMobile ? 300 : 500, overflow: 'auto', padding: 2 }}
      >
        <List>
          {filteredNotifications.map((notification) => (
            <ListItem
              key={notification.id}
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <ListItemText
                primary={notification.content}
                secondary={`From: ${notification.sender} | Type: ${notification.type}`}
              />
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Tooltip title='Mark as Read'>
                  <IconButton
                    color='primary'
                    onClick={() => handleMarkAsRead(notification.id)}
                  >
                    <MarkReadIcon />
                  </IconButton>
                </Tooltip>
                <Tooltip title='Delete Notification'>
                  <IconButton
                    color='error'
                    onClick={() => handleDeleteNotification(notification.id)}
                  >
                    <DeleteIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </ListItem>
          ))}
        </List>
      </Paper>

      {/* Notification Snackbar */}
      {notification && (
        <Snackbar
          open={Boolean(notification)}
          autoHideDuration={4000}
          onClose={() => setNotification(null)}
        >
          <Alert
            onClose={() => setNotification(null)}
            severity={notification.severity}
            sx={{ width: '100%' }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      )}
    </Box>
  );
};

export default PharmacistNotificationPage;
